import { useCallback, useMemo, useRef, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  TextField,
  FormHelperText,
  type SelectChangeEvent,
  Box,
} from "@mui/material";

interface SelectProps<T> {
  field: {
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
  };
  error?: boolean;
  helperText?: string;
  label?: string;
  options?: T[];
  idKey?: keyof T;
  nameKey?: keyof T;
  isMulti?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  enableSearch?: boolean;
  returnObject?: boolean;
  maxSelect?: number;
  required?: boolean;
  isSelectAll?: boolean;
  onChange?: (value: any) => void;
  acceptZero?: boolean;
}

const Select = <T extends Record<string, any>>({
  field,
  error,
  helperText,
  label,
  options = [],
  idKey = "value",
  nameKey = "label",
  isMulti = false,
  disabled = false,
  isLoading = false,
  loadingText = "Loading...",
  enableSearch = true,
  maxSelect,
  returnObject = false,
  required = false,
  isSelectAll = false,
  onChange,
  acceptZero = false,
}: SelectProps<T>) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedValue = field?.value ?? (isMulti ? [] : "");

  const filteredList = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return options;

    return options.filter((item) =>
      String(item?.[nameKey] ?? "")
        .toLowerCase()
        .includes(search)
    );
  }, [options, searchValue, nameKey]);

  const currentIds = useMemo(() => {
    if (isMulti) {
      return returnObject
        ? selectedValue?.map((x: any) => x?.[idKey])
        : selectedValue;
    }

    return returnObject ? selectedValue?.[idKey] : selectedValue;
  }, [selectedValue, isMulti, returnObject, idKey]);

  const safeValue = useMemo(() => {
    if (isMulti) {
      const ids = currentIds || [];
      return ids.filter((id: any) =>
        options.some((x) => String(x[idKey]) === String(id))
      );
    }

    if (acceptZero && currentIds === 0) {
      return 0;
    }

    if (
      currentIds === 0 ||
      currentIds === -1 ||
      currentIds === "" ||
      currentIds === null ||
      currentIds === undefined
    ) {
      return "";
    }

    return currentIds;
  }, [currentIds, options, isMulti, idKey]);

  const allSelected = isMulti && options.length > 0 &&
    currentIds?.length === options.length;

  const handleChange = (event: SelectChangeEvent<any>) => {
    const value = event.target.value;

    if (isMulti && maxSelect && value.length > maxSelect) return;

    if (!returnObject) {
      field.onChange(value);
      onChange?.(value);
      return;
    }

    if (isMulti) {
      const objs = options.filter((x) => value.includes(x[idKey]));
      field.onChange(objs);
    } else {
      const obj = options.find((x) => x[idKey] === value);
      field.onChange(obj || null);
    }
  };

  const renderValue = useCallback((selected: any) => {
    if (
      selected === undefined ||
      selected === null ||
      selected === "" ||
      (Array.isArray(selected) && selected.length === 0)
    ) return "";

    if (isMulti) {
      const ids = selected;
      const first = options.find((x) => String(x[idKey]) === String(ids[0]));
      const count = ids.length - 1;

      return count > 0
        ? `${first?.[nameKey]} (+${count} other${count > 1 ? "s" : ""})`
        : first?.[nameKey];
    }

    const obj = options.find((x) => String(x[idKey]) === String(selected));
    return obj?.[nameKey] ?? "";
  }, [options, isMulti, idKey, nameKey]);

  const handleSelectAll = (checked: boolean) => {
    if (!isMulti) return;
    if (checked) {
      if (returnObject) {
        field.onChange(options);
      } else {
        field.onChange(options.map(x => x[idKey]));
      }
    } else {
      field.onChange([]);
    }
  };

  return (
    <FormControl
      variant="standard"
      fullWidth
      required={required}
      error={!!error}
      disabled={disabled || isLoading}
      sx={{
        position: "relative",
        "& .MuiFormHelperText-root": {
          position: "absolute",
          top: "100%",
          left: 0,
          margin: 0,
          fontSize: "10px",
          lineHeight: "12px",
          whiteSpace: "nowrap",
        },
        "& .MuiInput-underline:before": {
          borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
        },
        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
          borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
        },
        "& .MuiInput-underline:after": {
          borderBottomColor: error ? "#d32f2f" : "#5145e5",
        },
      }}
    >
      {label && <InputLabel>{label}</InputLabel>}

      <MUISelect
        multiple={isMulti}
        value={isMulti ? safeValue ?? [] : safeValue ?? ""}
        onChange={handleChange}
        onBlur={() => field.onBlur?.()}
        displayEmpty
        renderValue={renderValue}
        sx={{
          color: error ? "#d32f2f" : "inherit",
          "& .MuiSelect-select": {
            color: error ? "#d32f2f" : "inherit",
          },
        }}
        onOpen={() => {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }}
        onClose={() => setSearchValue("")}
        MenuProps={{
          PaperProps: {
            elevation: 0,
            sx: {
              maxHeight: 220,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: { pt: 0 }
          }
        }}
        autoFocus={false}
      >
        {enableSearch && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#fff",
            }}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{
                "&.Mui-focusVisible": {
                  backgroundColor: "transparent",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {isMulti && isSelectAll && (
                <Checkbox
                  checked={allSelected}
                  indeterminate={
                    currentIds?.length > 0 &&
                    currentIds?.length < options.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              )}
              <TextField
                inputRef={searchInputRef}
                variant="standard"
                fullWidth
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </MenuItem>
          </Box>
        )}

        {isLoading ? (
          <MenuItem disabled>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            {loadingText}
          </MenuItem>
        ) : filteredList?.length > 0 ? (
          filteredList.map((opt) => {
            const value = opt[idKey];
            const optionLabel = opt[nameKey];

            const checked = isMulti
              ? currentIds?.map(String).includes(String(value))
              : String(currentIds) === String(value);

            return (
              <MenuItem
                key={String(value)}
                value={value}
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  "&.Mui-selected": !isMulti
                    ? {
                      backgroundColor: "primary.main",
                      color: "#fff",
                    }
                    : {},
                }}
              >
                {isMulti && <Checkbox checked={checked} />}
                <ListItemText primary={optionLabel} />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No results found</MenuItem>
        )}
      </MUISelect>

      {error && (<FormHelperText>{helperText}</FormHelperText>)}
    </FormControl>
  );
};

export default Select;
